import { useTranslations } from '@heidi/runtime/i18n';
import { AudioLines, File, PenLine } from 'lucide-react';
import { getOnboardingHost } from '../../../onboarding-host';
import { BlocksIcon } from './assets/blocks-svg';

// Re-export TemplateReviewContent from its own file
export { TemplateReviewContent } from './TemplateReviewContent';

// Shared skeleton components for preview panels
const SKELETON_TONES = {
  default: 'bg-background-primary-disabled',
  muted: 'bg-background-secondary',
  // theme-agnostic: reads against the guided-tour badge card, whose forest fill
  // is fixed brand artwork and does not flip with the theme.
  'on-forest': 'bg-forest-600',
} as const;

export type SkeletonTone = keyof typeof SKELETON_TONES;

export function SkeletonLine({
  width,
  variant = 'default',
}: {
  width: string;
  variant?: SkeletonTone;
}) {
  return (
    <div
      className={`h-2 animate-pulse rounded-full ${SKELETON_TONES[variant]} ${width}`}
    />
  );
}

export function SkeletonBlock({
  width,
  variant = 'default',
}: {
  width: string;
  variant?: SkeletonTone;
}) {
  return (
    <div
      className={`h-4 animate-pulse rounded-full ${SKELETON_TONES[variant]} ${width}`}
    />
  );
}

export function TemplatesListContent() {
  const { t } = useTranslations();

  return (
    <div className="relative flex size-full items-center justify-center overflow-hidden rounded-lg p-6">
      <div className="flex w-full max-w-[710px] flex-col gap-2 overflow-hidden rounded-2xl bg-surface-accent shadow-lg">
        <div className="flex items-center gap-2 pl-6 pt-6">
          <div className="flex h-9 w-[112px] items-center justify-center gap-1.5 rounded-lg px-3 py-2.5">
            <AudioLines size={20} className="text-accent-pink" />
            <span className="text-sm font-medium text-text-tertiary">
              {t('signup.previewTranscript')}
            </span>
          </div>
          <div className="flex h-9 items-center justify-center gap-1.5 rounded-lg bg-surface px-3 py-2.5">
            <PenLine size={20} className="text-accent-blue" />
            <span className="text-sm font-medium text-text-secondary">
              {t('signup.previewNote')}
            </span>
          </div>
          <div className="flex h-9 items-center gap-2 rounded-lg p-2">
            <File size={20} className="shrink-0 text-text-disabled" />
            <SkeletonLine width="w-[78px]" />
          </div>
          <div className="flex h-9 items-center gap-2 rounded-lg p-2">
            <File size={20} className="shrink-0 text-text-disabled" />
            <SkeletonLine width="w-[78px]" />
          </div>
        </div>

        <div className="mb-6 ml-6 mr-0 flex min-h-[535px] flex-col gap-6 rounded-l-2xl rounded-r-none border-2 border-r-0 border-border bg-surface p-6">
          <div className="flex h-11 items-center gap-1.5 self-start rounded-lg border border-border bg-surface px-3 py-2.5">
            <BlocksIcon size={20} className="text-text-primary" />
            <span className="text-lg font-medium text-text-primary">
              {t('signup.previewMyTemplate')}
            </span>
          </div>

          <div className="flex flex-col gap-12">
            <div className="flex flex-col gap-4">
              <SkeletonBlock width="w-[105px]" variant="muted" />
              <SkeletonBlock width="w-[353px]" variant="muted" />
              <SkeletonBlock width="w-full" variant="muted" />
              <SkeletonBlock width="w-[433px]" variant="muted" />
            </div>

            <div className="flex max-w-[413px] flex-col gap-4">
              <SkeletonBlock width="w-[105px]" variant="muted" />
              <div className="flex items-center gap-3">
                <div className="size-2 shrink-0 animate-pulse rounded-full bg-background-secondary" />
                <SkeletonBlock width="w-[394px]" variant="muted" />
              </div>
              <div className="flex items-center gap-3">
                <div className="size-2 shrink-0 animate-pulse rounded-full bg-background-secondary" />
                <SkeletonBlock width="w-[322px]" variant="muted" />
              </div>
              <div className="flex items-center gap-3">
                <div className="size-2 shrink-0 animate-pulse rounded-full bg-background-secondary" />
                <SkeletonBlock width="w-[380px]" variant="muted" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoadingAnimationContent() {
  const { TemplateAssemblyLoader } = getOnboardingHost();
  return (
    <div className="relative flex size-full items-center justify-center overflow-hidden rounded-lg p-6">
      <div className="flex h-[638px] w-full max-w-[710px] flex-col items-center justify-center rounded-lg border border-border bg-surface p-6">
        <TemplateAssemblyLoader />
      </div>
    </div>
  );
}
